import React, { useState, useCallback, useEffect, useRef } from 'react';
import '../Empresas/AgregarEmpresa.css';
import { ReactComponent as SubirArchivo } from '../assets/icon/subirarchivo.svg';
import { ReactComponent as Volver } from '../assets/icon/volver.svg';
import { useNavigate, useParams } from "react-router-dom";
import imageService from '../services/imageService';
import { getEmpresaById, createEmpresa, updateEmpresa } from '../services/empresas';
import { UPLOAD_STATUS, ERROR_MESSAGES } from '../utils';
import { useToast } from '../contexts/ToastContext';

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const MIN_WIDTH = 500;
const MIN_HEIGHT = 500;
const MAX_WIDTH = 1080;
const MAX_HEIGHT = 1080;

const validateImageFile = (file) => {
    if (!file) return { isValid: false, errors: [ERROR_MESSAGES.NO_FILE] };
    if (!file.type.startsWith("image/")) {
        return { isValid: false, errors: [ERROR_MESSAGES.INVALID_TYPE] };
    }
    if (file.size > MAX_FILE_SIZE) {
        return { isValid: false, errors: ['El archivo no debe exceder 1MB'] };
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const width = img.width;
            const height = img.height;
            const errors = [];
            if (width < MIN_WIDTH || height < MIN_HEIGHT) {
                errors.push(`La imagen debe ser mínimo ${MIN_WIDTH}x${MIN_HEIGHT}px`);
            }
            if (width > MAX_WIDTH || height > MAX_HEIGHT) {
                errors.push(`La imagen debe ser máximo ${MAX_WIDTH}x${MAX_HEIGHT}px`);
            }
            resolve({ isValid: errors.length === 0, errors });
        };
        img.onerror = () => {
            resolve({ isValid: false, errors: ['Error al procesar la imagen'] });
        };
        img.src = URL.createObjectURL(file);
    });
};

const ImageInfoModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="info-modal" ref={modalRef}>
                <h3>Medidas de Imagen Soportadas</h3>
                <ul>
                    <li><strong>Ancho:</strong> {MIN_WIDTH} - {MAX_WIDTH} pixels</li>
                    <li><strong>Alto:</strong> {MIN_HEIGHT} - {MAX_HEIGHT} pixels</li>
                    <li><strong>Peso máximo:</strong> 1MB</li>
                    <li><strong>Formatos:</strong> JPEG, PNG</li>
                </ul>
                <button className="modal-close-btn" onClick={onClose}>Cerrar</button>
            </div>
        </div>
    );
};

function AgregarEmpresa({ value, onChange: propOnChange }) {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const { showSuccess, showError } = useToast();

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(isEditMode);
    const [estado, setEstado] = useState("");
    const [preview, setPreview] = useState(null);
    const [nombre, setNombre] = useState("");
    const [promocion, setPromocion] = useState("");
    const [ubicacion, setUbicacion] = useState("");
    
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageUrl, setImageUrl] = useState("");
    const [imageFileName, setImageFileName] = useState("");
    const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const [showInfo, setShowInfo] = useState(false);

    const formRef = useRef(null);
    const nombreInputRef = useRef(null);
    const estadoSelectRef = useRef(null);
    const promocionTextareaRef = useRef(null);
    const ubicacionTextareaRef = useRef(null);
    const guardarButtonRef = useRef(null);
    const volverButtonRef = useRef(null);
    const logoUploadRef = useRef(null);

    useEffect(() => {
        if (isEditMode && id) {
            loadEmpresa(id);
        }
    }, [id, isEditMode]);

    const loadEmpresa = async (empresaId) => {
        setLoadingData(true);
        try {
            const result = await getEmpresaById(empresaId);
            if (result.success && result.error === 0 && result.data) {
                const empresa = result.data;
                setNombre(empresa.Nombre || "");
                setEstado(empresa.NombreEstado || "");
                setPromocion(empresa.Promocion || "");
                setUbicacion(empresa.Ubicacion || "");
                if (empresa.Url_File) {
                    setPreview(empresa.Url_File);
                    setImageUrl(empresa.Url_File);
                    setImageFileName(empresa.FileName || "");
                }
            } else {
                showError(result.mensaje || 'Error al cargar la empresa');
                navigate("/dashboard/empresas");
            }
        } catch (error) {
            showError('Error al cargar la empresa');
            navigate("/dashboard/empresas");
        } finally {
            setLoadingData(false);
        }
    };

    const handleFile = useCallback(async (file) => {
        if (!file) return;
        
        const validation = await validateImageFile(file);
        if (!validation.isValid) {
            setUploadError(validation.errors.join(', '));
            setUploadStatus(UPLOAD_STATUS.ERROR);
            return;
        }
        
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        
        try {
            setUploadStatus(UPLOAD_STATUS.UPLOADING);
            const result = await imageService.upload(file, {
                onProgress: setUploadProgress
            });
            
            if (result.success) {
                setImageUrl(result.url);
                setImageFileName(result.fileName);
                setUploadStatus(UPLOAD_STATUS.SUCCESS);
            } else {
                throw new Error(result.message || 'Error al subir imagen');
            }
        } catch (error) {
            setUploadError(error.message);
            setUploadStatus(UPLOAD_STATUS.ERROR);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        handleFile(e.dataTransfer.files[0]);
    }, [handleFile]);

    const removeImage = useCallback(async () => {
        if (imageFileName) {
            try {
                await imageService.deleteImage(imageFileName);
            } catch (error) {
                console.warn('No se pudo eliminar la imagen del servidor:', error.message);
            }
        }
        setPreview(null);
        setSelectedFile(null);
        setImageUrl("");
        setImageFileName("");
        setUploadStatus(UPLOAD_STATUS.IDLE);
        setUploadError("");
        setUploadProgress(0);
    }, [imageFileName]);

    const handleEstadoChange = (e) => {
        setEstado(e.target.value);
        if (propOnChange) propOnChange(e);
    };

    const validateForm = () => {
        if (!nombre.trim()) {
            showError('El nombre es requerido');
            return false;
        }
        if (!estado) {
            showError('El estado es requerido');
            return false;
        }
        return true;
    };

    const handleGuardar = async () => {
        if (!validateForm()) return;
        
        setLoading(true);
        try {
            const empresaData = {
                Nombre: nombre,
                NombreEstado: estado,
                Promocion: promocion,
                Ubicacion: ubicacion,
                url_File: imageUrl,
                FileName: imageFileName
            };

            let result;
            if (isEditMode) {
                result = await updateEmpresa(id, empresaData);
            } else {
                result = await createEmpresa(empresaData);
            }

            if (result.success && result.error === 0) {
                showSuccess(isEditMode ? 'Empresa actualizada correctamente' : 'Empresa creada correctamente');
                navigate("/dashboard/empresas");
            } else {
                showError(result.mensaje || 'Error al guardar la empresa');
            }
        } catch (error) {
            showError('Error al guardar la empresa');
        } finally {
            setLoading(false);
        }
    };

    const estadosMexico = [
        "Aguascalientes", "Baja California", "Baja California Sur", "Campeche",
        "Chiapas", "Chihuahua", "Ciudad de México", "Coahuila", "Colima",
        "Durango", "Estado de México", "Guanajuato", "Guerrero", "Hidalgo",
        "Jalisco", "Michoacán", "Morelos", "Nayarit", "Nuevo León", "Oaxaca",
        "Puebla", "Querétaro", "Quintana Roo", "San Luis Potosí", "Sinaloa",
        "Sonora", "Tabasco", "Tamaulipas", "Tlaxcala", "Veracruz", "Yucatán",
        "Zacatecas",
    ];

    if (loadingData) {
        return (
            <div className='bodyAgregarEmpresas'>
                <div className="loading-container">
                    <p>Cargando empresa...</p>
                </div>
            </div>
        );
    }

    return (
        <div className='bodyAgregarEmpresas' ref={formRef}>

            {/* TÍTULO */}
            <div className='tituloAgregarEmpresas'><p>{isEditMode ? '-Editar Empresa' : '-Nueva Empresa'}</p></div>

            <div className='cuerpoBotonVolverEmpresas'>
                <button
                    id="empresa-volver-btn"
                    ref={volverButtonRef}
                    className='botonesAgregarEmpresas'
                    onClick={() => navigate("/dashboard/empresas")}
                >
                    <div className="svgBotones"><Volver className="logo-svgBotones" /></div>
                    Volver
                </button>
            </div>

            <div className='cuerpoAgregarEmpresas'>
                {/* SUBIR IMAGEN */}
                <div className="logoUpload">
                    <div className="label-with-info">
                        <p className="label">Logo</p>
                        <button 
                            id="empresa-info-btn"
                            className="info-button" 
                            onClick={() => setShowInfo(true)}
                            type="button"
                            title="Ver medidas soportadas"
                        >
                            ?
                        </button>
                    </div>
                    <div
                        id="empresa-logo-upload"
                        ref={logoUploadRef}
                        className="uploadBox"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                    >
                        {preview ? (
                            <>
                                <img src={preview} alt="preview" className="preview" />

                                {/* BOTONES CAMBIAR / ELIMINAR */}
                                <div className="actions">
                                    <label id="empresa-cambiar-logo-btn" className="actionButton cambiar">
                                        Cambiar
                                        <input
                                            id="empresa-cambiar-logo-input"
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleFile(e.target.files[0])}
                                            hidden
                                        />
                                    </label>

                                    <button id="empresa-eliminar-logo-btn" className="actionButton eliminar" onClick={removeImage}>
                                        Eliminar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="iconSubirArchivo"><div className="svgSubirArchivo"><SubirArchivo className="logosvgSubirArchivo" /></div></div>
                                <p>Seleccione o arrastre aquí los archivos o</p>

                                <label id="empresa-seleccionar-logo-link" className="link">
                                    haga click para seleccionar
                                    <input
                                        id="empresa-seleccionar-logo-input"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleFile(e.target.files[0])}
                                        hidden
                                    />
                                </label>

                                <p>(Peso máximo 1MB)</p>
                            </>
                        )}
                    </div>
                </div>
                {/* DROPDOWN ESTADOS */}
                <div className='inputs'>
                    <div className='cuerpoinputAgregarEmpresas'>
                        <div className="dropdownContainer">
                            <div className="text-inputAgregarEmpresas">
                                <label className="dropdownLabel">Estado</label>
                            </div>

                            <div className="dropdownWrapper">
                                <select 
                                    id="empresa-estado-select"
                                    ref={estadoSelectRef}
                                    className="dropdownSelect" 
                                    value={estado} 
                                    onChange={handleEstadoChange}
                                >
                                    <option className='placeholderSeleccionarEstado' value="">
                                        Selecciona un estado
                                    </option>

                                    {estadosMexico.map((e) => (
                                        <option key={e} value={e}>{e}</option>
                                    ))}
                                </select>

                                <span className="dropdownIcon">▼</span>
                            </div>
                            <div className="contadorFantasma">0/30</div>
                        </div>
                    </div>
                    {/* INPUT NOMBRE + CONTADOR */}
                    {/* nombre */}
                    <div className="input-groupAgregarEmpresas">
                        <div className='text-inputAgregarEmpresas'>
                            <label htmlFor="empresa-nombre-input">Nombre</label>
                        </div>
                        <input
                            id="empresa-nombre-input"
                            ref={nombreInputRef}
                            className="inputAgregarEmpresas"
                            type="Text"
                            maxLength={100}
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="6B Invent Germany"
                        />
                        <div className="contador">{nombre.length}/100</div>
                    </div>
                    {/* promocion */}
                    <div className="input-groupAgregarEmpresas">
                        <div className='text-inputAgregarEmpresas'>
                            <label htmlFor="empresa-promocion-textarea">Promoción</label>
                        </div>
                        <textarea
                            id="empresa-promocion-textarea"
                            ref={promocionTextareaRef}
                            className="AgregarEmpresastextarea"
                            maxLength={160}
                            value={promocion}
                            onChange={(e) => setPromocion(e.target.value)}
                            placeholder="6B Invent Germany"
                            rows={1}
                        />
                        <div className="contador">{promocion.length}/160</div>
                    </div>
                    {/* ubicacion */}
                    <div className="input-groupAgregarEmpresas">
                        <div className='text-inputAgregarEmpresas'>
                            <label htmlFor="empresa-ubicacion-textarea">Ubicación</label>
                        </div>
                        <textarea
                            id="empresa-ubicacion-textarea"
                            ref={ubicacionTextareaRef}
                            className="AgregarEmpresastextareaUbicacion"
                            maxLength={70}
                            value={ubicacion}
                            onChange={(e) => setUbicacion(e.target.value)}
                            placeholder="6B Invent Germany"
                            rows={1}
                        />
                        <div className="contador">{ubicacion.length}/70</div>
                    </div>
                    {/* botones */}
                    {uploadStatus !== UPLOAD_STATUS.IDLE && (
                        <div className="upload-status">
                            {uploadStatus === UPLOAD_STATUS.UPLOADING && (
                                <div className="progress-container">
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                    <p className="progress-text">Subiendo imagen... {uploadProgress}%</p>
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.SUCCESS && (
                                <div className="success-message">
                                    <p>✓ Imagen subida exitosamente</p>
                                </div>
                            )}
                            
                            {uploadStatus === UPLOAD_STATUS.ERROR && (
                                <div className="error-message">
                                    <p>✗ {uploadError}</p>
                                    <button 
                                        id="empresa-reintentar-subida-btn"
                                        type="button" 
                                        className="retry-button"
                                        onClick={() => {
                                            if (selectedFile) handleFile(selectedFile);
                                        }}
                                    >
                                        Reintentar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <div className='botonesAgregarEmpresas2'>
                        <button 
                            id="empresa-guardar-btn"
                            ref={guardarButtonRef}
                            type="button" 
                            className="btn-acceptGuardarEmpresas"
                            onClick={handleGuardar}
                            disabled={loading}
                        >
                            {loading ? 'Guardando...' : 'Guardar'}
                        </button>
                    </div>
                </div>

            </div>
            <ImageInfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
        </div >
    );
}
export default AgregarEmpresa;
